import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  BookOpen, Award, Clock, ChevronRight, Lock, CheckCircle, 
  Play, FileText, LogOut, User, Settings, Users, BarChart3, Target, FolderOpen
} from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { getChapters, getProgress } from '@/services/api';
import { toast } from 'sonner';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, hasPaid, isAdmin } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasPaid && !isAdmin) {
      navigate('/enroll');
      return;
    }
    fetchData();
  }, [hasPaid, isAdmin]);

  const fetchData = async () => {
    try {
      const [chaptersRes, progressRes] = await Promise.all([
        getChapters(),
        getProgress()
      ]);
      setChapters(chaptersRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'in_progress': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status, isLocked) => {
    if (isLocked) return <Lock className="w-5 h-5" />;
    if (status === 'completed') return <CheckCircle className="w-5 h-5" />;
    return <Play className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 bg-card border-r border-border h-screen fixed left-0 top-0 flex flex-col">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Badge className="w-8 h-8" />
            <span className="font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              CBEC
            </span>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          <Link 
            to="/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-sm bg-primary/10 text-primary"
            data-testid="sidebar-dashboard-link"
          >
            <BarChart3 className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/chapters" 
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-secondary transition-colors"
            data-testid="sidebar-chapters-link"
          >
            <BookOpen className="w-5 h-5" />
            Chapters
          </Link>
          <Link 
            to="/recovery-board" 
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-secondary transition-colors"
            data-testid="sidebar-recovery-link"
          >
            <Users className="w-5 h-5" />
            Recovery Board
          </Link>
          <Link 
            to="/practice-test" 
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-secondary transition-colors"
            data-testid="sidebar-practice-test-link"
          >
            <Target className="w-5 h-5" />
            Practice Test
          </Link>
          <Link 
            to="/my-records" 
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-secondary transition-colors"
            data-testid="sidebar-my-records-link"
          >
            <FolderOpen className="w-5 h-5" />
            My Records
          </Link>
          {progress?.certificate_status === 'approved' && (
            <Link 
              to="/certificate" 
              className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-secondary transition-colors"
              data-testid="sidebar-certificate-link"
            >
              <Award className="w-5 h-5" />
              Certificate
            </Link>
          )}
          {(progress?.certificate_status === 'approved' || isAdmin) && (
            <Link 
              to="/id-card" 
              className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-secondary transition-colors"
              data-testid="sidebar-id-card-link"
            >
              <User className="w-5 h-5" />
              ID Card {isAdmin && !progress?.certificate_status ? '(Preview)' : ''}
            </Link>
          )}
          {isAdmin && (
            <Link 
              to="/admin" 
              className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-secondary transition-colors text-accent"
              data-testid="sidebar-admin-link"
            >
              <Settings className="w-5 h-5" />
              Admin Panel
            </Link>
          )}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <User className="w-5 h-5 text-muted-foreground" />
            <div className="text-sm">
              <div className="font-medium">{user?.first_name} {user?.last_name}</div>
              <div className="text-muted-foreground text-xs">{user?.email}</div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            onClick={handleLogout}
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-black mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              WELCOME BACK, <span className="text-primary">{user?.first_name?.toUpperCase()}</span>
            </h1>
            <p className="text-muted-foreground">
              Continue your certification journey
            </p>
          </div>

          {/* Progress Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-primary" />
                </div>
                <span className="text-muted-foreground text-sm">Progress</span>
              </div>
              <div className="text-3xl font-black text-primary" style={{ fontFamily: 'Barlow Condensed' }}>
                {progress?.completion_percentage || 0}%
              </div>
              <Progress value={progress?.completion_percentage || 0} className="mt-2 h-2" />
            </div>

            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-primary" />
                </div>
                <span className="text-muted-foreground text-sm">Chapters</span>
              </div>
              <div className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>
                {progress?.completed_chapters || 0}/{progress?.total_chapters || 10}
              </div>
              <p className="text-sm text-muted-foreground mt-1">Completed</p>
            </div>

            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <span className="text-muted-foreground text-sm">Time</span>
              </div>
              <div className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>
                {progress?.total_time_formatted || '0m'}
              </div>
              <p className="text-sm text-muted-foreground mt-1">Logged</p>
            </div>

            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                  <Award className="w-5 h-5 text-primary" />
                </div>
                <span className="text-muted-foreground text-sm">Certificate</span>
              </div>
              <div className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                {progress?.certificate_status === 'approved' ? (
                  <span className="text-green-500">APPROVED</span>
                ) : progress?.certificate_status === 'pending_review' ? (
                  <span className="text-primary">PENDING</span>
                ) : (
                  <span className="text-muted-foreground">NOT STARTED</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {progress?.certificate_number || 'Complete course'}
              </p>
            </div>
          </div>

          {/* Chapters List */}
          <div className="bg-card border border-border rounded-sm">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                COURSE CHAPTERS
              </h2>
            </div>
            <div className="divide-y divide-border">
              {/* Course Introduction entry */}
              <div
                className="p-6 flex items-center gap-4 hover:bg-secondary/50 cursor-pointer transition-colors"
                onClick={() => navigate('/course-introduction')}
                data-testid="course-introduction-entry"
              >
                <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-primary/10">
                  <BookOpen className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Welcome</span>
                  </div>
                  <h3 className="font-semibold mt-1">Course Introduction</h3>
                  <p className="text-sm text-muted-foreground mt-1">Why this course exists — and why it matters</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
              {chapters.map((chapter, index) => (
                <React.Fragment key={chapter.id}>
                  {/* Ken Hill Case Study — Chapter 5 Entry Point */}
                  {chapter.chapter_number === 5 && (
                    <div 
                      className="p-6 flex items-center gap-4 hover:bg-secondary/50 cursor-pointer transition-colors bg-accent/5 border-l-4 border-accent"
                      onClick={() => navigate('/case-study/ken-hill')}
                      data-testid="ken-hill-case-study-row"
                    >
                      <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-accent/10">
                        <FileText className="w-5 h-5 text-accent" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-accent text-sm uppercase font-semibold">Chapter 5 Entry Point · Case Study</span>
                        </div>
                        <h3 className="font-semibold text-lg">State v. Ken Hill</h3>
                        <p className="text-sm text-muted-foreground">
                          The four-chapter chassis, applied to the record • Story · Evidence · Investigation · Assessment
                        </p>
                      </div>

                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div 
                    className={`p-6 flex items-center gap-4 ${chapter.is_locked ? 'opacity-60' : 'hover:bg-secondary/50 cursor-pointer'} transition-colors`}
                    onClick={() => !chapter.is_locked && navigate(`/chapter/${chapter.id}`)}
                    data-testid={`chapter-${chapter.chapter_number}`}
                  >
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                      chapter.status === 'completed' ? 'bg-green-500/10' :
                      chapter.status === 'in_progress' ? 'bg-primary/10' : 'bg-secondary'
                    }`}>
                      <span className={getStatusColor(chapter.status)}>
                        {getStatusIcon(chapter.status, chapter.is_locked)}
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-sm">Chapter {chapter.chapter_number}</span>
                        {chapter.status === 'completed' && (
                          <span className="px-2 py-0.5 bg-green-500/10 text-green-500 text-xs rounded-sm uppercase">
                            Complete
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-lg">{chapter.title}</h3>
                      {chapter.time_spent_seconds > 0 && (
                        <p className="text-sm text-muted-foreground">
                          {Math.floor(chapter.time_spent_seconds / 60)}m logged
                        </p>
                      )}
                    </div>

                    {!chapter.is_locked && (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </React.Fragment>
              ))}

              {/* Final Exam */}
              <div 
                className={`p-6 flex items-center gap-4 ${
                  (progress?.completed_chapters < 10 && !isAdmin) ? 'opacity-60' : 'hover:bg-secondary/50 cursor-pointer'
                } transition-colors`}
                onClick={() => (progress?.completed_chapters >= 10 || isAdmin) && navigate('/final-exam')}
                data-testid="final-exam-row"
              >
                <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                  progress?.final_exam_passed ? 'bg-green-500/10' : 'bg-primary/10'
                }`}>
                  <Award className={`w-5 h-5 ${progress?.final_exam_passed ? 'text-green-500' : 'text-primary'}`} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-primary text-sm uppercase font-semibold">Final Certification Exam</span>
                    {progress?.final_exam_passed && (
                      <span className="px-2 py-0.5 bg-green-500/10 text-green-500 text-xs rounded-sm uppercase">
                        Passed
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-lg">150 Questions • Proctored</h3>
                  <p className="text-sm text-muted-foreground">
                    3 Hour Time Limit • 75% Passing Score Required
                  </p>
                </div>

                {(progress?.completed_chapters >= 10 || isAdmin) ? (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <Lock className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
